import { useContext, useEffect, useState } from 'react';
import { Box, Chip, Tab, Tabs, Typography, useMediaQuery } from '@mui/material';
import { UserDetailsContext } from '../../../context/user-details/user-details';
import { SettingsMobileNavbar } from '../../../root-components/navigation/settings-mobile-navbar';
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading';
import { TableDisplay } from '../../presentation-components/miscellaneous-components/table-display';
import { TableActionButtons } from '../../presentation-components/miscellaneous-components/table-action-buttons';
import { LoadingPage } from '../loading-page';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import { IHeadCell } from '../../../shared/interfaces/table-display.model';
import { IconName } from '../../../shared/interfaces/icon-picker.model';
import gaiaTheme from '../../../root-components/theme';
import ScheduleService from '../../../shared/services/schedule/schedule.service';
import { ISchedule } from '../../../shared/services/schedule/schedule.model';
import { ScheduleDialog } from '../../dialogs/settings/schedule-dialog';

const headCells: IHeadCell[] = [
  { id: 'name', numeric: false, label: 'Name', sortable: false, isHeading: true },
  { id: 'type', numeric: false, label: 'Type', sortable: false },
  { id: 'frequency', numeric: false, label: 'Frequency', sortable: false },
  { id: 'nextRun', numeric: false, label: 'Next run', sortable: false },
  { id: 'lastRun', numeric: false, label: 'Last run', sortable: false },
  { id: 'status', numeric: false, label: 'Status', sortable: false },
  { id: 'action', numeric: false, label: '', sortable: false },
];

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function frequencyLabel(schedule: ISchedule): string {
  if (schedule.scheduleType === 'one_off') return 'Once';
  switch (schedule.frequency) {
    case 'weekly': {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const day = schedule.dayOfWeek != null ? days[schedule.dayOfWeek] : '';
      return `Weekly · ${day} ${schedule.runTime || ''}`.trim();
    }
    case 'monthly':
      return `Monthly · Day ${schedule.dayOfMonth ?? '—'} ${schedule.runTime || ''}`.trim();
    case 'custom': {
      const mins = schedule.customIntervalMinutes ?? 0;
      if (mins >= 24 * 60 && mins % (24 * 60) === 0) return `Every ${mins / (24 * 60)} day(s)`;
      return `Every ${mins / 60} hour(s)`;
    }
    default:
      return schedule.frequency || '—';
  }
}

function scheduleStatus(schedule: ISchedule): 'active' | 'paused' | 'completed' {
  if (schedule.active) return 'active';
  if (schedule.nextRunAt) return 'paused';
  return 'completed';
}

export const SettingsSchedulesPage = () => {
  const userNotificationsContext = useContext(UserNotificationsContext);
  const { userDetails } = useContext(UserDetailsContext);
  const isAdmin = userDetails?.roles?.includes('admin') ?? false;
  const scheduleService = new ScheduleService();
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

  const [schedules, setSchedules] = useState<ISchedule[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [inputPage, setInputPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [change, setChange] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const [tab, setTab] = useState<'active' | 'inactive'>('active');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editSchedule, setEditSchedule] = useState<ISchedule | null>(null);

  useEffect(() => {
    loadSchedules();
  }, [inputPage, perPage, change]);

  const loadSchedules = () => {
    setIsLoading(true);
    scheduleService.getAll({ page: inputPage, perPage })
      .then(r => {
        setSchedules(r.rows);
        setTotalRecords(r.count);
      })
      .catch(() => {
        userNotificationsContext.openSnackbar({ open: true, message: 'Failed to load schedules', backgroundColor: 'text.primary' });
      })
      .finally(() => setIsLoading(false));
  };

  const handlePause = (schedule: ISchedule) => {
    scheduleService.pause(schedule.id)
      .then(() => {
        userNotificationsContext.openSnackbar({
          open: true,
          message: schedule.active ? 'Schedule paused' : 'Schedule resumed',
          backgroundColor: 'primary.dark',
        });
        setChange(c => c + 1);
      })
      .catch(() => {
        userNotificationsContext.openSnackbar({ open: true, message: 'Failed to update schedule', backgroundColor: 'text.primary' });
      });
  };

  const handleRunNow = (schedule: ISchedule) => {
    scheduleService.runNow(schedule.id)
      .then(() => {
        userNotificationsContext.openSnackbar({ open: true, message: 'Schedule triggered successfully', backgroundColor: 'primary.dark' });
        setChange(c => c + 1);
      })
      .catch(() => {
        userNotificationsContext.openSnackbar({ open: true, message: 'Failed to trigger schedule', backgroundColor: 'text.primary' });
      });
  };

  const handleDelete = (schedule: ISchedule) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Delete schedule',
      message: [
        `Are you sure you want to delete "${schedule.name}"?`,
        'This will also delete all actions attached to this schedule and cannot be undone.',
      ],
      buttonCopy: 'Delete',
      cancelButtonCopy: 'Cancel',
    }).then(confirmed => {
      if (!confirmed) return;
      scheduleService.remove(schedule.id)
        .then(() => {
          userNotificationsContext.openSnackbar({ open: true, message: 'Schedule deleted', backgroundColor: 'primary.dark' });
          setChange(c => c + 1);
        })
        .catch(() => {
          userNotificationsContext.openSnackbar({ open: true, message: 'Failed to delete schedule', backgroundColor: 'text.primary' });
        });
    });
  };

  const filteredSchedules = schedules.filter(s => {
    const status = scheduleStatus(s);
    return tab === 'active'
      ? status === 'active' || status === 'paused'
      : status === 'completed';
  });

  const rows = filteredSchedules.map(schedule => ({
    id: schedule.id,
    name: <Typography variant='body2' fontWeight={500}>{schedule.name}</Typography>,
    type: (
      <Chip
        size='small'
        label={schedule.scheduleType === 'one_off' ? 'One-off' : 'Recurring'}
        color={schedule.scheduleType === 'one_off' ? 'default' : 'primary'}
        variant='outlined'
      />
    ),
    frequency: <Typography variant='body2'>{frequencyLabel(schedule)}</Typography>,
    nextRun: <Typography variant='body2'>{formatDateTime(schedule.nextRunAt)}</Typography>,
    lastRun: <Typography variant='body2'>{formatDateTime(schedule.lastRunAt)}</Typography>,
    status: (() => {
      const s = scheduleStatus(schedule);
      return (
        <Chip
          size='small'
          label={s === 'active' ? 'Active' : s === 'paused' ? 'Paused' : 'Completed'}
          color={s === 'active' ? 'success' : s === 'paused' ? 'warning' : 'default'}
        />
      );
    })(),
    action: (
      <span onClick={e => e.stopPropagation()}>
        <TableActionButtons
          id={schedule.id}
          includeEdit={isAdmin}
          onEdit={() => { setEditSchedule(schedule); setDialogOpen(true); }}
          additionalMenuItems={[
            ...(isAdmin ? [
              {
                label: schedule.active ? 'Pause' : 'Resume',
                onClick: () => handlePause(schedule),
              },
              {
                label: 'Run now',
                onClick: () => handleRunNow(schedule),
              },
              {
                label: 'Delete',
                onClick: () => handleDelete(schedule),
              },
            ] : []),
          ]}
        />
      </span>
    ),
  }));

  return (
    <PageOuterContainer>
      {isMobileMode && <Box sx={{ pb: 4 }}><SettingsMobileNavbar /></Box>}
      <PageHeading
        title='Schedules'
        hasTabs={false}
        includeActionButton={isAdmin}
        actionButtonIcon={IconName.Add}
        actionButtonText='New schedule'
        actionButtonHandler={() => { setEditSchedule(null); setDialogOpen(true); }}
      />
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label='Active' value='active' />
          <Tab label='Inactive' value='inactive' />
        </Tabs>
      </Box>

      <LoadingPage pageReady={!isLoading}>
        <TableDisplay
          isMobileMode={isMobileMode}
          headCells={headCells}
          rows={rows}
          selected={[]}
          setSelected={() => {}}
          inputPage={inputPage}
          setInputPage={setInputPage}
          totalRecords={filteredSchedules.length}
          perPage={perPage}
          setPerPage={setPerPage}
        />
      </LoadingPage>

      <ScheduleDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditSchedule(null); }}
        successHandler={() => { setDialogOpen(false); setEditSchedule(null); setChange(c => c + 1); }}
        editSchedule={editSchedule}
      />
    </PageOuterContainer>
  );
};
